import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@offsilo/ui/toast";
import { m } from "@/paraglide/messages.js";
import { emitRecordEvent } from "@/tours/events";
import { capacityKeysFor } from "./capacityKeys";
import { client, type CollectionName, type Patch, type Row } from "./client";
import { serverAuth } from "./auth";

export const keys = {
  workspace: ["workspace"] as const,
  list: (c: CollectionName) => ["list", c] as const,
  chrome: (c: CollectionName) => ["chrome", c] as const,
  row: (c: CollectionName, id: string) => ["row", c, id] as const,
};

/** A row with changes applied; null clears a field (the server sends null in Undo patches), so it is removed.
 *  `custom` (custom field values) merges key by key, as the server does, so saving one field keeps the others. */
function merge<T>(row: T, changes: Partial<T>): T {
  const out = { ...row, ...changes } as Record<string, unknown>;
  for (const [k, v] of Object.entries(changes)) if (v === null) delete out[k];
  const before = (row as Record<string, unknown>).custom as Record<string, unknown> | undefined;
  const next = (changes as Record<string, unknown>).custom as Record<string, unknown> | undefined;
  if (next && typeof next === "object") {
    const custom: Record<string, unknown> = { ...(before ?? {}) };
    for (const [k, v] of Object.entries(next)) if (v === null) delete custom[k]; else custom[k] = v;
    out.custom = custom;
  }
  return out as T;
}

export function useMetrics(week: "this" | "last" | "next" = "this") {
  return useQuery({ queryKey: ["metrics", week], queryFn: () => client.metrics(week), staleTime: 60_000 });
}

export function useWorkspace() {
  return useQuery({ queryKey: keys.workspace, queryFn: () => client.workspace(), staleTime: Number.POSITIVE_INFINITY });
}

/** The workspace a sign-in page is for. A signed-out visitor cannot read /workspace (it answered 401 and left the portal
 *  sign-in on its error state), so server mode reads the single tenant's public name and slug (GET /api/public/tenant). */
export function useSignInWorkspace() {
  return useQuery({
    queryKey: ["sign-in", "workspace"],
    queryFn: async (): Promise<{ name: string; slug: string }> => {
      if (!serverAuth) {
        const ws = await client.workspace();
        return { name: ws.name, slug: ws.slug };
      }
      const res = await fetch("/api/public/tenant");
      if (!res.ok) throw new Error(`tenant ${res.status}`);
      return (await res.json()) as { name: string; slug: string };
    },
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/** Shell data (rail, sidebar, topbar). It ignores ?state= so the shell always renders around a forced page state. */
export function useChromeCollection<C extends CollectionName>(collection: C) {
  return useQuery({ queryKey: keys.chrome(collection), queryFn: () => client.list(collection, { chrome: true }), staleTime: 30_000 });
}

/** A whole collection, cached; filter in the page with useMemo (the sample set is small). */
export function useCollection<C extends CollectionName>(collection: C, opts?: { enabled?: boolean; archived?: boolean }) {
  // archived (notifications): the archived rows, read from the server under their own cache entry (X21-OPS-24)
  const archived = opts?.archived === true;
  return useQuery({
    queryKey: archived ? ([...keys.list(collection), "archived"] as const) : keys.list(collection),
    queryFn: () => client.list(collection, archived ? { archived: true } : undefined),
    staleTime: 30_000,
    enabled: opts?.enabled ?? true,
  });
}

/** The rows the server selects for a filter tree (the fg parameter, X13-0101a). Off while there is no server filter. */
export function useFilteredCollection<C extends CollectionName>(collection: C, fg: string | null) {
  return useQuery({ queryKey: [...keys.list(collection), "fg", fg], queryFn: () => client.list(collection, { fg: fg ?? undefined }), enabled: fg !== null, staleTime: 30_000 });
}

export function useRecord<C extends CollectionName>(collection: C, id: string | undefined) {
  return useQuery({
    queryKey: keys.row(collection, id ?? ""),
    queryFn: () => client.get(collection, id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/** Optimistic update with an Undo toast (DESIGN-RULES 9: writes are optimistic; a failure rolls back with a toast). */
export function useUpdate<C extends CollectionName>(collection: C) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Omit<Patch<C>, "collection">) => client.update({ ...patch, collection }),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: keys.list(collection) });
      const before = qc.getQueryData<Row<C>[]>(keys.list(collection));
      qc.setQueryData<Row<C>[]>(keys.list(collection), (rows) => rows?.map((r) => ((r as { id: string }).id === patch.id ? merge(r, patch.changes as Partial<Row<C>>) : r)));
      return { before };
    },
    onError: (err, _patch, ctx) => {
      if (ctx?.before) qc.setQueryData(keys.list(collection), ctx.before);
      // the server says what was wrong (a 422 names the field); show that, not just "could not save"
      const body = (err as { body?: { detail?: string; fields?: { message: string }[] } }).body;
      toast({ title: m.toast_save_failed(), description: body?.fields?.[0]?.message ?? body?.detail, tone: "danger" });
    },
    onSettled: (_d, _e, patch) => {
      qc.invalidateQueries({ queryKey: keys.row(collection, patch.id) });
      // a server-filtered list (fg) may gain or lose this row, so it refetches; the plain list is patched above
      qc.invalidateQueries({ queryKey: [...keys.list(collection), "fg"] });
      // a move and its Undo both settle here, so the people load and the computed capacity follow each of them (X11-326a)
      for (const queryKey of capacityKeysFor(collection)) qc.invalidateQueries({ queryKey });
    },
  });
}

/** Run an update and offer Undo in a toast. */
export function useUpdateWithUndo<C extends CollectionName>(collection: C) {
  const update = useUpdate(collection);
  return (patch: Omit<Patch<C>, "collection">, title: string) =>
    update.mutate(patch, {
      onSuccess: (inverse) => toast({ title, action: { label: m.action_undo(), onAction: () => update.mutate({ id: inverse.id, changes: inverse.changes }) } }),
    });
}

/** Adds a row optimistically at the top of the cached list. */
export function useCreate<C extends CollectionName>(collection: C) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (row: Omit<Row<C>, "id"> & { id?: string }) => client.create(collection, row),
    // a list never loaded is left alone (seeding it with one row would show a partial list until the next fetch)
    onSuccess: (row) => {
      emitRecordEvent({ c: collection, id: String((row as { id: string }).id), op: "c" });
      qc.setQueryData<Row<C>[]>(keys.list(collection), (rows) => (rows ? [row, ...rows] : rows));
    },
    onError: () => toast({ title: m.toast_save_failed(), tone: "danger" }),
  });
}

/** Removes a row and offers Undo in a toast. */
export function useRemoveWithUndo<C extends CollectionName>(collection: C) {
  const qc = useQueryClient();
  const create = useCreate(collection);
  const remove = useMutation({
    mutationFn: (id: string) => client.remove(collection, id),
    onSuccess: (row) => qc.setQueryData<Row<C>[]>(keys.list(collection), (rows) => rows?.filter((r) => (r as { id: string }).id !== (row as { id: string }).id)),
    onError: () => toast({ title: m.toast_save_failed(), tone: "danger" }),
  });
  return (id: string, title: string) =>
    remove.mutate(id, { onSuccess: (row) => toast({ title, action: { label: m.action_undo(), onAction: () => create.mutate(row as Omit<Row<C>, "id"> & { id?: string }) } }) });
}
